import { useRef, useEffect, useState } from 'react'
import { ExternalLink, RotateCcw, Eye, Film } from 'lucide-react'

interface Props {
  html: string | null
  clipUrl: string | null
  status: string
}

type Mode = 'live' | 'clip'

export default function PreviewPanel({ html, clipUrl, status }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [mode, setMode] = useState<Mode>(clipUrl ? 'clip' : 'live')
  const [replayKey, setReplayKey] = useState(0)

  // Auto-flip to clip view when a clip arrives
  useEffect(() => {
    if (clipUrl && status === 'recorded') setMode('clip')
  }, [clipUrl, status])

  // Write HTML into iframe in live mode
  useEffect(() => {
    if (mode !== 'live') return
    if (!iframeRef.current || !html) return
    const doc = iframeRef.current.contentDocument
    if (doc) {
      doc.open()
      doc.write(html)
      doc.close()
    }
  }, [html, mode, replayKey])

  const handleReplay = () => setReplayKey(k => k + 1)

  const handleViewFullPage = () => {
    if (!html) return
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  }

  return (
    <div className="flex flex-1 flex-col bg-gray-50">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Preview</span>
          {clipUrl && (
            <div className="ml-2 flex items-center gap-1 rounded border border-gray-200 bg-gray-50 p-0.5">
              <button
                onClick={() => setMode('live')}
                className={`flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                  mode === 'live' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Eye className="h-3 w-3" />
                Live
              </button>
              <button
                onClick={() => setMode('clip')}
                className={`flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                  mode === 'clip' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Film className="h-3 w-3" />
                MP4
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {mode === 'live' && html && (
            <>
              <button
                onClick={handleReplay}
                className="flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-gray-900"
                title="Replay animation"
              >
                <RotateCcw className="h-3 w-3" />
                Replay
              </button>
              <button
                onClick={handleViewFullPage}
                className="flex items-center gap-1 text-xs text-blue-500 transition-colors hover:text-blue-600"
              >
                Open Full Page
                <ExternalLink className="h-3 w-3" />
              </button>
            </>
          )}
          {mode === 'clip' && clipUrl && (
            <a
              href={clipUrl}
              download
              className="flex items-center gap-1 text-xs text-blue-500 transition-colors hover:text-blue-600"
            >
              Download
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>

      {/* Mobile frame */}
      <div className="flex flex-1 items-center justify-center overflow-auto p-8">
        <div
          className="relative rounded-[36px] bg-black p-[6px] shadow-[0_20px_60px_-12px_rgba(0,0,0,0.35)]"
          style={{ width: 402, height: 856 }}
        >
          {/* Inner bezel */}
          <div className="relative h-full w-full overflow-hidden rounded-[30px] bg-black">
            {mode === 'live' ? (
              html ? (
                <iframe
                  key={replayKey}
                  ref={iframeRef}
                  className="h-full w-full border-0"
                  style={{ width: 390, height: 844 }}
                  sandbox="allow-scripts allow-same-origin"
                  title="Demo preview"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-100 text-center text-sm text-gray-400" style={{ width: 390, height: 844 }}>
                  <div>
                    <p className="mb-2">No HTML yet</p>
                    <p className="text-xs">Send a message to generate a demo</p>
                  </div>
                </div>
              )
            ) : clipUrl ? (
              <video
                key={clipUrl}
                src={clipUrl}
                className="h-full w-full bg-black"
                style={{ width: 390, height: 844 }}
                autoPlay
                loop
                muted
                playsInline
                controls
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-900 text-sm text-gray-400" style={{ width: 390, height: 844 }}>
                No clip yet
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
